import { IconChevronLeft24, IconChevronRight24 } from '@warp-ds/icons/react';
import type React from 'react';
import type { CSSProperties } from 'react';
import { useCallback, useEffect, useRef, useState } from 'react';
import type { AdImage, LightboxState, SmoothType } from '../types';
import { debounce, generateSrcSet, getLowResImageUrl, loadImage, mod } from '../utils';

type MainCarouselProps = {
    showBlur: boolean;
    currentImage: number;
    images: AdImage[];
    nextImage: (e?: React.UIEvent | undefined) => void;
    previousImage: (e?: React.UIEvent | undefined) => void;
    smooth: SmoothType;
    fallbackUrl: string | undefined;
    programmaticRatioOverride?: boolean;
    lightboxState: LightboxState;
    delayF: (currentImage: number) => void;
    programmaticRatio: boolean;
    useBackground?: boolean;
    roundAllCorners: boolean;
    noImageCount: boolean;
    newLightbox?: boolean;
    showHud?: boolean;
    tab?: string;
    children?: React.ReactNode;
    onImageClick?: () => void;
};
type State = {
    listElement?: HTMLUListElement;
    ulStyle: CSSProperties;
    sizes: string;
};

const NavigationButtons: React.FC<{
    onPrevious: (e?: React.UIEvent) => void;
    onNext: (e?: React.UIEvent) => void;
}> = ({ onPrevious, onNext }) => (
    <>
        <div className={`button-left-container`}>
            <button
                type="button"
                aria-label="gå til forrige bilde"
                className="button-left flex justify-center items-center"
                onClick={onPrevious}>
                <IconChevronLeft24 strokeWidth={2} />
            </button>
        </div>
        <div className={`button-right-container`}>
            <button
                type="button"
                aria-label="gå til neste bilde"
                className="button-right flex justify-center items-center "
                onClick={onNext}>
                <IconChevronRight24 strokeWidth={2} />
            </button>
        </div>
    </>
);

export const MainCarousel = (props: MainCarouselProps) => {
    const {
        showBlur,
        currentImage,
        images,
        nextImage,
        previousImage,
        lightboxState,
        fallbackUrl,
        delayF,
        smooth,
        programmaticRatio,
        useBackground,
        roundAllCorners,
        noImageCount,
        newLightbox,
        children,
        showHud,
        tab,
    } = props;
    const totalImages = images.length;

    const initialStyle = {
        aspectRatio: '3/2',
    };
    const initialSizes = '(min-width: 1020px) 980px';

    const initialState = {
        sizes: initialSizes,
        ulStyle: initialStyle,
    };

    const [state, setState] = useState<State>(initialState);
    const [zooming, setZooming] = useState(false);

    const setScrollPosition = (currentImage: number) => {
        if (state.listElement?.clientWidth) {
            const newPos = (currentImage || 0) * state.listElement.clientWidth;
            if (state.listElement.scrollLeft !== newPos) {
                state.listElement.scrollLeft = (currentImage || 0) * state.listElement.clientWidth;
            }
        }
    };

    const checkScrollPosition = () => {
        if (!state.listElement?.clientWidth) {
            return;
        }
        const scrollPosition = state.listElement.scrollLeft ?? 0;
        const currentImage = Math.min(Math.round(scrollPosition / state.listElement.clientWidth), images.length);
        loadImage(currentImage);
        loadImage(currentImage + 1);
        snapChecker(currentImage, state.listElement, setScrollPosition);
        delayF(currentImage);
    };

    useEffect(() => {
        const listener = () => {
            setScrollPosition(currentImage);
        };
        window.addEventListener('resize', listener);
        return () => {
            window.removeEventListener('resize', listener);
        };
    }, [state.listElement, currentImage]);

    useEffect(() => {
        if (state.listElement?.clientWidth) {
            const notScrolling = (): boolean => {
                const list = state.listElement;
                if (!list) return false;
                const clientWidth = list.clientWidth;
                if (!clientWidth) return false;
                const close = list.scrollLeft % clientWidth;

                // Width is typically 984, but sometimes it becomes a decimal such as
                // 983.4583211 or 0.359382 (after mod). So we need to compare these
                // to the width and 0.
                return Math.ceil(close) === clientWidth || Math.floor(close) === 0;
            };

            if (smooth !== 'smooth' || notScrolling()) {
                setScrollPosition(currentImage);
            }

            let styleChange: CSSProperties | undefined;
            const [sizes, styles] =
                lightboxState === 'big-image'
                    ? [
                          '',
                          {
                              // Allow to take full height, minus size of top (36px), minus size of info at bottom (38px).
                              // maxHeight: newLightbox ? 'calc(100vh - 124px)' : 'calc(100vh - 74px)',
                              maxHeight: newLightbox ? '' : 'calc(100vh - 74px)',
                          },
                      ]
                    : [initialSizes, initialStyle];

            if (programmaticRatio) {
                styleChange = {
                    height: `${state.listElement.clientWidth * 0.75}px`,
                    // aspectRatio: '4 / 3' is 3/4 = 0.75
                };
            }
            setState((s) => ({
                ...s,
                ulStyle: { ...styles, ...styleChange },
                sizes: sizes,
            }));
        }
    }, [lightboxState, currentImage, state.listElement, tab]);

    const callbackRef = useCallback(
        (callbackElement: HTMLUListElement) => {
            setState((s) => ({
                ...s,
                listElement: callbackElement,
            }));
        },
        [lightboxState],
    );

    useEffect(() => {
        loadImage(mod(currentImage, totalImages));
        loadImage(mod(currentImage + 1, totalImages));
        if (currentImage !== 0) {
            loadImage(mod(currentImage + 2, totalImages));
        }
        loadImage(mod(currentImage - 1, totalImages));
    }, [currentImage]);

    const scrollStyle = props.smooth === 'no-scroll' || zooming ? 'overflow-x-hidden' : 'overflow-x-auto';

    const roundedCorners = lightboxState === 'closed' || newLightbox ? (roundAllCorners ? 'md:rounded-8' : 'md:rounded-b-8') : null;

    const ulStyles = state.ulStyle;

    return (
        <div className={newLightbox ? 'relative' : ''}>
            <a
                href={fallbackUrl || '#'}
                aria-live="polite"
                aria-label={`${images[currentImage]?.description || ''}. Bilde ${currentImage + 1} av ${totalImages}`}
                onClick={(e) => {
                    e.preventDefault();
                    props.onImageClick?.();
                }}
                onKeyDown={(e) => {
                    if (e.key === ' ' || e.key === 'Spacebar' || e.key === 'Space') {
                        e.preventDefault();
                        props.onImageClick?.();
                    }
                }}>
                <ul
                    tabIndex={-1}
                    className={`carousel ${scrollStyle}
                        ${newLightbox && lightboxState === 'big-image' ? 'gallery-remaining-with-padding' : ''}
                        ${useBackground && lightboxState === 'closed' ? 'ul-no-filter' : ''} flex overscroll-x-contain overflow-y-hidden w-full mb-0`}
                    style={ulStyles}
                    ref={callbackRef}
                    id="main-carousel"
                    onScroll={checkScrollPosition}>
                    {showBlur && <div className={`absolute inset-0 img-with-blur ${roundedCorners}`} />}
                    {images.map((image, index) => (
                        <CarouselListItem
                            image={image}
                            index={index}
                            currentImage={currentImage}
                            lightboxState={lightboxState}
                            showBlur={showBlur}
                            roundedCorners={roundedCorners}
                            roundAllCorners={roundAllCorners}
                            sizes={state.sizes}
                            images={images}
                            newLightbox={newLightbox}
                            key={`main-image-${image.uri}`}
                            setZooming={setZooming}
                        />
                    ))}
                </ul>
                {newLightbox && lightboxState !== 'closed' && showHud && (
                    <div className="absolute right-0 top-0 z-10 sm:top-24 mx-24">
                        <div className={`image-description p-8 rounded-4 ml-auto s-text-inverted`}>
                            <span>
                                {currentImage + 1}/{totalImages}
                            </span>
                        </div>
                    </div>
                )}
                {newLightbox || lightboxState === 'closed' ? (
                    <div
                        className={`absolute bottom-0 left-0 right-0 text-center z-10 text-transition ${
                            lightboxState === 'closed' ? 'mr-16 mb-12 md:mr-24 md:mb-20 justify-end' : 'flex sm:mb-24 justify-center mx-24'
                        } ${noImageCount ? 'hidden sm:flex' : 'flex'}`}>
                        <div className={`w-full flex justify-between s-text-inverted`}>
                            {images[currentImage]?.description && lightboxState !== 'closed' && (
                                <div className="image-description p-8 rounded-4 mr-16" style={showHud ? { marginRight: '100px' } : {}}>
                                    <span>{images[currentImage]?.description}</span>
                                </div>
                            )}
                            <div
                                className={`image-description p-8 rounded-4 ml-auto ${
                                    lightboxState !== 'closed' && newLightbox && showHud && 'hidden'
                                }`}>
                                <span>
                                    {currentImage + 1}/{totalImages}
                                </span>
                            </div>
                        </div>
                    </div>
                ) : (
                    <div className="absolute flex justify-center bottom-0 left-0 right-0 mx-10 text-center sm:bottom-32">
                        <div className={`image-description max-w-full rounded-8 p-8 flex s-text-inverted `}>
                            <span>{images[currentImage]?.description}</span>
                            <span>
                                &nbsp;({currentImage + 1}/{totalImages})
                            </span>
                        </div>
                    </div>
                )}
            </a>
            {images.length > 1 && <NavigationButtons onPrevious={previousImage} onNext={nextImage} />}
            {children}
        </div>
    );
};

type CarouselListItemProps = {
    image: AdImage;
    index: number;
    currentImage: number;
    lightboxState: LightboxState;
    roundedCorners: string | null;
    showBlur: boolean;
    roundAllCorners: boolean;
    sizes: string;
    images: AdImage[];
    newLightbox?: boolean;
    setZooming: (zooming: boolean) => void;
};

const CarouselListItem = ({
    image,
    index,
    currentImage,
    lightboxState,
    roundedCorners,
    showBlur,
    roundAllCorners,
    sizes,
    images,
    newLightbox,
    setZooming,
}: CarouselListItemProps) => {
    const [currentSrc, setCurrentSrc] = useState('');
    const [scale, setScale] = useState(1);
    const [pointX, setPointX] = useState(0);
    const [pointY, setPointY] = useState(0);
    const [start, setStart] = useState({ x: 0, y: 0 });
    const [isPanning, setIsPanning] = useState(false);
    const [isPinching, setIsPinching] = useState(false);
    const [initialDistance, setInitialDistance] = useState(0);
    const [doubleTap, setDoubleTap] = useState(false);

    const hasLoaded = useRef(false);
    const liRef = useRef<HTMLLIElement>(null);
    const imgRef = useRef<HTMLImageElement>(null);

    const superImage = hasLoaded.current ? image : images[currentImage];

    useEffect(() => {
        hasLoaded.current = true;
    }, []);

    useEffect(() => {
        if (lightboxState === 'big-image' && currentImage === index) {
            const imgElement = imgRef.current;
            if (!imgElement) return;

            const wheelHandler = (e: WheelEvent) => {
                wheelEvent(e);
            };

            imgElement.addEventListener('wheel', wheelHandler, { passive: false });

            return () => {
                imgElement.removeEventListener('wheel', wheelHandler);
            };
        }
    }, [scale, pointX, pointY, currentImage]);

    useEffect(() => {
        if (scale !== 1) {
            setScale(1);
            setPointX(0);
            setPointY(0);
        }
    }, [currentImage]);

    const wheelEvent = (e: WheelEvent) => {
        if (e.ctrlKey) {
            e.stopPropagation();
            e.preventDefault();
        }

        const rect = liRef.current?.getBoundingClientRect();
        if (!rect) return;

        if (Math.abs(e.deltaY) < Math.abs(e.deltaX)) {
            return;
        }

        const x = e.clientX - rect.left;
        const y = e.clientY - rect.top;

        const dx = (x - pointX) / scale;
        const dy = (y - pointY) / scale;
        const delta = e.deltaY ? -e.deltaY : e.deltaMode;

        const newScale = delta > 0 ? scale * 1.05 : scale / 1.05;
        if (newScale <= 1) {
            setZooming(false);
            setScale(1);
            setPointX(0);
            setPointY(0);
        } else if (newScale >= 4) {
            return;
        } else {
            setZooming(true);
            setScale(newScale);
            setPointX(x - dx * newScale);
            setPointY(y - dy * newScale);
        }
    };

    const mouseMoving = (e: React.MouseEvent) => {
        e.preventDefault();
        if (!isPanning) {
            return;
        }
        const containerRect = liRef.current?.getBoundingClientRect();
        const imgRect = imgRef.current?.getBoundingClientRect();
        if (!containerRect || !imgRect) return;

        const newPointX = e.clientX - start.x;
        const newPointY = e.clientY - start.y;

        if (!(newPointX > 0 || newPointX - containerRect.width < -imgRect.width)) {
            setPointX(newPointX);
        }

        if (!(newPointY > 0 || newPointY - containerRect.height < -imgRect.height)) {
            setPointY(newPointY);
        }
    };

    const mouseDown = (e: React.MouseEvent) => {
        e.preventDefault();
        setStart({ x: e.clientX - pointX, y: e.clientY - pointY });
        setIsPanning(true);
    };

    const handleTouchStart = useCallback(
        (e: TouchEvent) => {
            if (doubleTap) {
                if (e.touches.length === 1) {
                    e.preventDefault();
                    if (scale !== 1) {
                        setZooming(false);
                        setScale(1);
                        setPointX(0);
                        setPointY(0);
                    } else {
                        const touch = e.touches[0];
                        const newScale = 2;
                        const rect = liRef.current?.getBoundingClientRect();
                        if (!rect) return;
                        const x = touch.clientX - rect.left;
                        const y = touch.clientY - rect.top;
                        const dx = (x - pointX) / scale;
                        const dy = (y - pointY) / scale;

                        setZooming(true);
                        setScale(newScale);
                        setPointX(x - dx * newScale);
                        setPointY(y - dy * newScale);
                    }
                }
            } else {
                setDoubleTap(true);
                setTimeout(() => {
                    setDoubleTap(false);
                }, 300);
            }
            if (e.touches.length === 1 && scale !== 1) {
                const touch = e.touches[0];
                setStart({ x: touch.clientX - pointX, y: touch.clientY - pointY });
                setIsPanning(true);
            } else if (e.touches.length === 2) {
                e.preventDefault();

                const touch1 = e.touches[0];
                const touch2 = e.touches[1];

                const x = (touch1.clientX + touch2.clientX) / 2;
                const y = (touch1.clientY + touch2.clientY) / 2;

                setInitialDistance(getDistance(touch1, touch2));
                setStart({ x: x, y: y });
                setIsPinching(true);
            }
        },
        [pointX, pointY, scale, doubleTap],
    );

    const handleTouchMove = useCallback(
        (e: TouchEvent) => {
            if (isPanning && e.touches.length === 1) {
                e.preventDefault();
                const containerRect = liRef.current?.getBoundingClientRect();
                const imgRect = imgRef.current?.getBoundingClientRect();
                if (!containerRect || !imgRect) return;
                const touch = e.touches[0];

                const newPointX = touch.clientX - start.x;
                const newPointY = touch.clientY - start.y;

                if (!(newPointX > 0 || newPointX - containerRect.width < -imgRect.width)) {
                    setPointX(newPointX);
                }

                if (!(newPointY > 0 || newPointY - containerRect.height < -imgRect.height)) {
                    setPointY(newPointY);
                }
            } else if (isPinching && e.touches.length === 2) {
                e.preventDefault();
                e.stopPropagation();

                const touch1 = e.touches[0];
                const touch2 = e.touches[1];

                const rect = liRef.current?.getBoundingClientRect();
                if (!rect) return;

                const currentDistance = getDistance(touch1, touch2);
                const deltaScale = currentDistance / initialDistance;

                const newScale = scale * deltaScale;

                if (newScale <= 1) {
                    setZooming(false);
                    setScale(1);
                    setPointX(0);
                    setPointY(0);
                } else if (newScale >= 4) {
                    return;
                } else {
                    const x = (touch1.clientX + touch2.clientX) / 2 - rect.left;
                    const y = (touch1.clientY + touch2.clientY) / 2 - rect.top;
                    const scaleRatio = newScale / scale;
                    const newPointX = scaleRatio * pointX + x * (1 - scaleRatio);
                    const newPointY = scaleRatio * pointY + y * (1 - scaleRatio);
                    setZooming(true);
                    setInitialDistance(currentDistance);
                    setScale(newScale);
                    setPointX(newPointX);
                    setPointY(newPointY);
                }
            }
        },
        [isPanning, isPinching, pointX, pointY, scale, start, initialDistance],
    );

    const handleTouchEnd = useCallback(() => {
        setIsPanning(false);
        setIsPinching(false);
    }, []);

    const getDistance = (touch1: Touch, touch2: Touch) => {
        const dx = touch1.clientX - touch2.clientX;
        const dy = touch1.clientY - touch2.clientY;
        return Math.hypot(dx, dy);
    };

    useEffect(() => {
        const imgElement = imgRef.current;
        if (!imgElement || lightboxState !== 'big-image' || currentImage !== index) return;

        imgElement.addEventListener('touchmove', handleTouchMove, { passive: false });
        imgElement.addEventListener('touchstart', handleTouchStart, { passive: false });
        imgElement.addEventListener('touchend', handleTouchEnd);

        return () => {
            imgElement.removeEventListener('touchmove', handleTouchMove);
            imgElement.removeEventListener('touchstart', handleTouchStart);
            imgElement.removeEventListener('touchend', handleTouchEnd);
        };
    }, [handleTouchMove, handleTouchStart, handleTouchEnd, currentImage, lightboxState]);

    return (
        <li
            className={`carousel-item flex shrink-0 w-full ${roundedCorners}`}
            ref={liRef}
            style={
                showBlur
                    ? {
                          backgroundColor: '#fff',
                          backgroundImage: currentSrc
                              ? `url(${currentSrc})`
                              : images[currentImage]
                                ? `url(${getLowResImageUrl(images[currentImage].uri)})`
                                : '',
                          backgroundPosition: 'center',
                          backgroundRepeat: 'no-repeat',
                          backgroundSize: 'cover',
                      }
                    : {}
            }>
            <img
                key={`main-image-${image.uri}`}
                onLoad={(e) => {
                    const target = e.target as HTMLImageElement;
                    setCurrentSrc(target.currentSrc);
                }}
                ref={imgRef}
                className={`max-h-full max-w-full w-full object-center ${currentImage === index ? 'carousel-with-transition' : ''} ${
                    showBlur && superImage && Math.abs(superImage.width / superImage.height - 1.5) < 0.05 && !newLightbox
                        ? 'object-cover'
                        : 'object-contain'
                }
                                    ${
                                        lightboxState === 'closed' || newLightbox
                                            ? roundAllCorners
                                                ? 'md:rounded-8'
                                                : 'md:rounded-b-8 border-except-top'
                                            : ''
                                    }`}
                title={image.description || ''}
                id={`image-${index}`}
                sizes={sizes}
                style={
                    showBlur
                        ? {
                              zIndex: 1,
                              transformOrigin: '0px 0px',
                              transform:
                                  scale === 1 || lightboxState !== 'big-image'
                                      ? 'none'
                                      : `translate(${pointX}px, ${pointY}px) scale(${scale})`,
                          }
                        : {}
                }
                onMouseDown={mouseDown}
                onMouseUp={() => setIsPanning(false)}
                onMouseMove={(e) => {
                    if (scale > 1 && isPanning) {
                        mouseMoving(e);
                    }
                }}
                srcSet={superImage && generateSrcSet(superImage.uri)}
                data-srcset={generateSrcSet(image.uri)}
                alt="Galleribilde"
            />
        </li>
    );
};

const checkSnapPos = (currentImage: number, listElement: HTMLUListElement, setScrollPosition: (currentImage: number) => void) => {
    const outOfSync = listElement.scrollLeft % listElement.clientWidth !== 0;
    if (outOfSync) {
        setScrollPosition(currentImage);
    }
};

const [snapChecker] = debounce(checkSnapPos, 1200);
